import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Award, PenLine, Download, ListPlus } from "lucide-react";
import { openFile } from "@/lib/fileViewer";
import { SignersDialog } from "@/components/certificates/SignersDialog";

const ayDefault = () => { const d = new Date(); const y = d.getMonth() >= 8 ? d.getFullYear() : d.getFullYear() - 1; return `${y}/${String(y + 1).slice(2)}`; };

const EMPTY = [];

function Field({ label, children }) {
  return <div><Label className="text-[10px] uppercase tracking-widest text-stone-500">{label}</Label><div className="mt-1">{children}</div></div>;
}

export default function CertGenerator() {
  const qc = useQueryClient();
  const [f, setF] = useState({ course_id: "", course_label: "", title: "", academic_year: ayDefault(), date: new Date().toLocaleDateString("en-CA"), director_id: "", teacher_id: "", publish: true });
  const [sel, setSel] = useState(new Set());
  const [signersOpen, setSignersOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const { data: courses = [] } = useQuery({ queryKey: ["courses-all"], queryFn: async () => (await api.get("/courses")).data });
  const { data: titles } = useQuery({ queryKey: ["cert-titles"], queryFn: async () => (await api.get("/cert-gen/titles")).data.titles });
  const { data: signers = [] } = useQuery({ queryKey: ["cert-signers"], queryFn: async () => (await api.get("/cert-gen/signers")).data });
  const { data: students = EMPTY } = useQuery({
    queryKey: ["cert-eligible", f.course_id],
    queryFn: async () => (await api.get("/cert-gen/eligible", { params: { course_id: f.course_id } })).data,
    enabled: !!f.course_id,
  });

  useEffect(() => { if (titles?.length && !f.title) setF((x) => ({ ...x, title: titles[0] })); }, [titles]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setSel(new Set(students.filter((s) => !s.has_certificate).map((s) => s.id))); }, [students]);

  const pickCourse = (id) => { const c = courses.find((x) => x.id === id); setF({ ...f, course_id: id, course_label: c?.name || "" }); setLast(null); };
  const payload = () => ({ ...f, director_id: f.director_id || null, teacher_id: f.teacher_id || null, student_ids: [...sel] });
  const toggle = (id, on) => setSel((s) => { const n = new Set(s); on ? n.add(id) : n.delete(id); return n; });

  async function editTitles() {
    const v = window.prompt("Tipi di attestato (uno per riga separati da ;)", (titles || []).join("; "));
    if (v == null) return;
    try { await api.put("/cert-gen/titles", { titles: v.split(";") }); qc.invalidateQueries({ queryKey: ["cert-titles"] }); toast.success("Elenco aggiornato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  }
  async function preview() {
    try { const r = await api.post("/cert-gen/preview", payload(), { responseType: "blob" }); openFile(URL.createObjectURL(r.data), "Anteprima attestato.pdf"); }
    catch { toast.error("Errore anteprima"); }
  }
  async function generate() {
    if (!window.confirm(`Generare ${sel.size} attestati${f.publish ? " e pubblicarli agli studenti" : ""}?`)) return;
    setBusy(true);
    try {
      const r = (await api.post("/cert-gen/generate", payload())).data;
      setLast(r); toast.success(`${r.count} attestati generati`);
      qc.invalidateQueries({ queryKey: ["cert-eligible", f.course_id] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  }
  async function downloadZip() {
    const r = await api.get(`/cert-gen/batches/${last.batch_id}/zip`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "attestati.zip"; a.click();
  }

  const signerSel = (key, role) => (
    <Select value={f[key] || "__none"} onValueChange={(v) => setF({ ...f, [key]: v === "__none" ? "" : v })}>
      <SelectTrigger data-testid={`cert-${role}`}><SelectValue/></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none">— Nessuno —</SelectItem>
        {signers.filter((s) => s.role === role).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.signature_path ? "" : " (senza firma)"}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1300px] mx-auto" data-testid="cert-generator-page">
      <PageHeader title="Genera attestati" subtitle="Scegli corso o seminario, tipo di attestato e firmatari: genera in blocco gli attestati per residenziali e modulari."/>
      <div className="grid lg:grid-cols-[420px_1fr] gap-6">
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none space-y-4">
          <Field label="Corso / seminario">
            <Select value={f.course_id} onValueChange={pickCourse}>
              <SelectTrigger data-testid="cert-course"><SelectValue placeholder="Seleziona…"/></SelectTrigger>
              <SelectContent>{courses.filter((c) => c.status !== "archiviato").map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.track_type ? ` · ${c.track_type}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Testo percorso formativo"><Input value={f.course_label} onChange={(e) => setF({ ...f, course_label: e.target.value })} placeholder="Es. Inglese - A1" data-testid="cert-course-label"/></Field>
          <Field label="Tipo di attestato">
            <div className="flex gap-2">
              <Select value={f.title} onValueChange={(v) => setF({ ...f, title: v })}>
                <SelectTrigger data-testid="cert-title"><SelectValue/></SelectTrigger>
                <SelectContent>{(titles || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={editTitles} title="Modifica elenco" data-testid="cert-edit-titles"><ListPlus size={15}/></Button>
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Anno accademico"><Input value={f.academic_year} onChange={(e) => setF({ ...f, academic_year: e.target.value })} data-testid="cert-ay"/></Field>
            <Field label="Data attestato"><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="cert-date"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Direttore">{signerSel("director_id", "direttore")}</Field>
            <Field label="Docente">{signerSel("teacher_id", "docente")}</Field>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSignersOpen(true)} data-testid="cert-manage-signers"><PenLine size={13} className="mr-1"/> Gestisci firmatari e firme</Button>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.publish} onCheckedChange={(v) => setF({ ...f, publish: !!v })} data-testid="cert-publish"/> Pubblica nell'area Attestati dello studente (con notifica)</label>
        </Card>

        <Card className="border border-stone-200 rounded-xl shadow-none flex flex-col" data-testid="cert-students">
          <div className="p-4 border-b border-stone-100 flex items-center gap-3 flex-wrap">
            <div className="flex-1 text-sm font-medium">Studenti <span className="text-stone-400 font-normal">· selezionati {sel.size}/{students.length}</span></div>
            {students.length > 0 && <Button variant="ghost" size="sm" onClick={() => setSel(sel.size === students.length ? new Set() : new Set(students.map((s) => s.id)))} data-testid="cert-toggle-all">{sel.size === students.length ? "Deseleziona tutti" : "Seleziona tutti"}</Button>}
          </div>
          <div className="flex-1 max-h-[55vh] overflow-y-auto divide-y divide-stone-50">
            {!f.course_id ? <div className="p-10 text-center text-sm text-stone-400">Scegli un corso per vedere gli studenti.</div>
              : students.length === 0 ? <div className="p-10 text-center text-sm text-stone-400">Nessuno studente attivo per questo corso.</div>
              : students.map((s) => (
                <label key={s.id} className="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-stone-50" data-testid={`cert-stud-${s.id}`}>
                  <Checkbox checked={sel.has(s.id)} onCheckedChange={(v) => toggle(s.id, !!v)}/>
                  <span className="flex-1">{s.last_name} {s.first_name}</span>
                  <span className="text-[10px] text-stone-400">{s.track_type}{s.year_level ? ` · ${s.year_level}° anno` : ""}</span>
                  {s.has_certificate && <span className="text-[10px] rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2">già emesso</span>}
                </label>
              ))}
          </div>
          <div className="p-4 border-t border-stone-100 flex flex-wrap gap-2 justify-end">
            {last && <Button variant="outline" onClick={downloadZip} data-testid="cert-zip"><Download size={14} className="mr-1"/> Scarica ZIP ({last.count})</Button>}
            <Button variant="outline" onClick={preview} disabled={!f.course_label || !f.title} data-testid="cert-preview"><Eye size={14} className="mr-1"/> Anteprima</Button>
            <Button onClick={generate} disabled={busy || !sel.size || !f.course_label || !f.title} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="cert-generate">
              <Award size={14} className="mr-1"/> {busy ? "Generazione…" : `Genera ${sel.size} attestati`}
            </Button>
          </div>
        </Card>
      </div>
      <SignersDialog open={signersOpen} onOpenChange={setSignersOpen}/>
    </div>
  );
}
